import { useEffect, useState } from 'preact/hooks';
import { MainLayout } from '../../components/MainLayout';
import { useScopeContext } from '../../contexts/ScopeContext';
import {
  skillsService,
  SkillListItem,
  SkillDetail,
  SKILL_DESCRIPTION_MAX_LENGTH,
  estimateSkillTokens,
} from '../../services/skills.service';
import { renderMarkdown } from '../../utils/markdown';
import { toast } from 'react-toastify';

type SortField = 'name' | 'updatedAt';
type SortDirection = 'asc' | 'desc';

/**
 * Descriptions for every skill live in the agent's prompt permanently.
 * These thresholds turn that invisible cost into something visible before
 * it becomes a problem.
 */
const CONTEXT_BUDGET = {
  /** Comfortable ceiling; past this the index starts crowding the prompt. */
  SOFT_LIMIT_TOKENS: 5000,
  /** Past this, skill selection accuracy degrades. */
  HARD_LIMIT_TOKENS: 10000,
};

export function SkillsPage() {
  const scope = useScopeContext();
  const [skills, setSkills] = useState<SkillListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('updatedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Detail state
  const [selected, setSelected] = useState<SkillDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<SkillListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Create state
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newBody, setNewBody] = useState('');
  const [savingNew, setSavingNew] = useState(false);

  useEffect(() => {
    loadSkills();
  }, [scope.teamId]);

  const loadSkills = async () => {
    try {
      setLoading(true);
      setError(null);
      setSkills(await skillsService.listSkills(scope.teamId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  };

  // Always-on context cost: this is the number that matters as skills accumulate
  const totalTokens = skills.reduce((sum, s) => sum + estimateSkillTokens(s), 0);
  const budgetState =
    totalTokens >= CONTEXT_BUDGET.HARD_LIMIT_TOKENS
      ? 'error'
      : totalTokens >= CONTEXT_BUDGET.SOFT_LIMIT_TOKENS
        ? 'warning'
        : 'info';

  const agentAuthored = skills.filter((s) => s.createdBy === 'agent').length;

  const visibleSkills = skills
    .filter((s) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const cmp =
        sortField === 'name'
          ? a.name.localeCompare(b.name)
          : new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      return sortDirection === 'asc' ? cmp : -cmp;
    });

  const openCreate = () => {
    setNewName('');
    setNewDescription('');
    setNewBody('');
    setCreating(true);
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newDescription.trim() || !newBody.trim()) {
      toast.error('Name, description, and instructions are all required');
      return;
    }
    if (newDescription.length > SKILL_DESCRIPTION_MAX_LENGTH) {
      toast.error(`Description must be ${SKILL_DESCRIPTION_MAX_LENGTH} characters or fewer`);
      return;
    }

    setSavingNew(true);
    try {
      const result = await skillsService.createSkill(scope.teamId, {
        name: newName.trim(),
        description: newDescription.trim(),
        body: newBody,
      });
      if (result.success && result.data) {
        toast.success(`Created "${result.data.name}"`);
        setCreating(false);
        loadSkills();
      } else {
        // Surfaces the API's validation message (kebab-case, length caps, duplicates)
        toast.error(result.error || 'Failed to create skill');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create skill');
    } finally {
      setSavingNew(false);
    }
  };

  const openDetail = async (skill: SkillListItem) => {
    setLoadingDetail(true);
    setEditing(false);
    try {
      const detail = await skillsService.getSkill(scope.teamId, skill.id);
      if (!detail) {
        toast.error('Skill content not found');
        return;
      }
      setSelected(detail);
      setEditedDescription(detail.description);
      setEditedBody(detail.body);
    } catch (err) {
      toast.error('Failed to load skill');
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setEditing(false);
  };

  const handleSave = async () => {
    if (!selected) return;

    if (editedDescription.length > SKILL_DESCRIPTION_MAX_LENGTH) {
      toast.error(`Description must be ${SKILL_DESCRIPTION_MAX_LENGTH} characters or fewer`);
      return;
    }

    const updates: { description?: string; body?: string } = {};
    if (editedDescription !== selected.description) updates.description = editedDescription;
    if (editedBody !== selected.body) updates.body = editedBody;

    if (Object.keys(updates).length === 0) {
      setEditing(false);
      return;
    }

    setSaving(true);
    try {
      const result = await skillsService.updateSkill(scope.teamId, selected.id, updates);
      if (result.success && result.data) {
        toast.success(
          updates.body
            ? `Saved as v${result.data.version}. The previous version was kept.`
            : 'Description updated'
        );
        setSelected({ ...selected, ...result.data, body: editedBody });
        setEditing(false);
        loadSkills();
      } else {
        toast.error(result.error || 'Failed to update skill');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update skill');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await skillsService.deleteSkill(scope.teamId, deleteTarget.id);
      if (result.success) {
        toast.success(`Deleted "${deleteTarget.name}"`);
        if (selected?.id === deleteTarget.id) closeDetail();
        setDeleteTarget(null);
        loadSkills();
      } else {
        toast.error(result.error || 'Failed to delete skill');
      }
    } catch (err) {
      toast.error('Failed to delete skill');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div className="container mx-auto p-4">
            <div className="alert alert-error">
              <span>{error}</span>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div class="flex-1 overflow-auto">
        <div className="container mx-auto p-4">
          {/* Header - matches ConnectionsPage: title block left, actions right */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold">Skills</h1>
              <p className="text-base-content/70 mt-1">
                Procedures your agents recall on their own. Each skill's description stays in the
                agent's context; the full instructions load only when a request matches.
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <a href={`${scope.getNavigationPrefix()}/files`} className="btn btn-ghost">
                Team Files
              </a>
              <button className="btn btn-primary" onClick={openCreate}>
                New Skill
              </button>
            </div>
          </div>

          {skills.length === 0 ? (
            <div className="border-2 border-dashed border-base-300 rounded-2xl p-12 text-center">
              <div className="text-6xl mb-4">🧠</div>
              <h3 className="text-2xl font-semibold mb-2">No skills yet</h3>
              <p className="text-base-content/70 max-w-md mx-auto mb-6">
                Skills are usually created in chat: tell an agent how you want something done —
                "always CC billing@ on refund replies" — and ask it to remember. It saves the
                procedure here and uses it in future conversations. You can also write one yourself.
              </p>
              <button className="btn btn-primary" onClick={openCreate}>
                New Skill
              </button>
            </div>
          ) : (
            <>
              {/* Context budget - the thing that quietly grows */}
              {/* Class names are spelled out, not interpolated, so Tailwind keeps them */}
              <div
                className={`alert mb-4 ${
                  budgetState === 'error'
                    ? 'alert-error'
                    : budgetState === 'warning'
                      ? 'alert-warning'
                      : 'alert-info'
                }`}
              >
                <div className="flex-1">
                  <div className="font-medium">
                    {skills.length} skill{skills.length === 1 ? '' : 's'} · ~
                    {totalTokens.toLocaleString()} tokens always in agent context
                  </div>
                  <div className="text-sm opacity-80 mt-0.5">
                    {agentAuthored > 0 && `${agentAuthored} written by an agent. `}
                    {budgetState === 'error'
                      ? 'Past this many descriptions, agents start picking the wrong skill. Delete or merge overlapping ones.'
                      : budgetState === 'warning'
                        ? 'Getting large. Review for overlapping skills.'
                        : 'Bodies are excluded — they load only when a skill is used.'}
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search skills..."
                  className="input input-bordered w-full max-w-md"
                  value={searchQuery}
                  onInput={(e) => setSearchQuery((e.target as HTMLInputElement).value)}
                />
              </div>

              {/* Table */}
              <div className="overflow-x-auto border border-base-300 rounded-xl">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="cursor-pointer" onClick={() => toggleSort('name')}>
                        Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th>Triggers when</th>
                      <th>Version</th>
                      <th className="cursor-pointer" onClick={() => toggleSort('updatedAt')}>
                        Updated {sortField === 'updatedAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSkills.map((skill) => (
                      <tr key={skill.id} className="hover">
                        <td>
                          <button
                            className="font-mono font-medium link link-hover text-left"
                            onClick={() => openDetail(skill)}
                          >
                            {skill.name}
                          </button>
                          {skill.createdBy === 'agent' && (
                            <span className="badge badge-ghost badge-sm ml-2">agent</span>
                          )}
                        </td>
                        <td className="max-w-md">
                          <span className="text-sm text-base-content/80">{skill.description}</span>
                        </td>
                        <td className="text-sm">v{skill.version}</td>
                        <td className="text-sm whitespace-nowrap">{formatDate(skill.updatedAt)}</td>
                        <td className="text-right">
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={() => setDeleteTarget(skill)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {visibleSkills.length === 0 && (
                <p className="text-center text-base-content/60 mt-6">
                  No skills match "{searchQuery}"
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Detail modal */}
      {(selected || loadingDetail) && (
        <div className="modal modal-open">
          <div className="modal-box max-w-3xl">
            {loadingDetail || !selected ? (
              <div className="flex justify-center p-8">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-start mb-1">
                  <h3 className="font-mono font-bold text-lg">{selected.name}</h3>
                  <button className="btn btn-ghost btn-sm" onClick={closeDetail}>
                    ✕
                  </button>
                </div>
                <p className="text-sm text-base-content/60 mb-4">
                  v{selected.version} · updated {formatDate(selected.updatedAt)} by{' '}
                  {selected.updatedBy === 'agent' ? 'an agent' : 'a team member'}
                </p>

                {editing ? (
                  <>
                    <label className="label">
                      <span className="label-text font-medium">Triggers when</span>
                      <span
                        className={`label-text-alt ${
                          editedDescription.length > SKILL_DESCRIPTION_MAX_LENGTH
                            ? 'text-error'
                            : 'text-base-content/60'
                        }`}
                      >
                        {editedDescription.length}/{SKILL_DESCRIPTION_MAX_LENGTH}
                      </span>
                    </label>
                    <textarea
                      className="textarea textarea-bordered w-full mb-4"
                      rows={2}
                      value={editedDescription}
                      onInput={(e) =>
                        setEditedDescription((e.target as HTMLTextAreaElement).value)
                      }
                    />

                    <label className="label">
                      <span className="label-text font-medium">Instructions</span>
                    </label>
                    <textarea
                      className="textarea textarea-bordered w-full font-mono text-sm"
                      rows={16}
                      value={editedBody}
                      onInput={(e) => setEditedBody((e.target as HTMLTextAreaElement).value)}
                    />

                    <div className="modal-action">
                      <button
                        className="btn btn-ghost"
                        onClick={() => {
                          setEditedDescription(selected.description);
                          setEditedBody(selected.body);
                          setEditing(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className={`btn btn-primary ${saving ? 'loading' : ''}`}
                        disabled={saving}
                        onClick={handleSave}
                      >
                        Save
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-4">
                      <div className="text-xs uppercase text-base-content/60 mb-1">
                        Triggers when
                      </div>
                      <p className="text-sm">{selected.description}</p>
                    </div>

                    <div className="text-xs uppercase text-base-content/60 mb-1">Instructions</div>
                    {/* Matches the chat renderer's markdown container - there is no
                        typography plugin, so `prose` would be a no-op class. */}
                    <div
                      className="text-sm max-w-none bg-base-200 rounded-lg p-4 max-h-96 overflow-auto [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(selected.body) }}
                    />

                    <div className="modal-action">
                      <button
                        className="btn btn-ghost text-error"
                        onClick={() => setDeleteTarget(selected)}
                      >
                        Delete
                      </button>
                      <button className="btn btn-primary" onClick={() => setEditing(true)}>
                        Edit
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <div className="modal-backdrop" onClick={closeDetail}></div>
        </div>
      )}

      {/* Create */}
      {creating && (
        <div className="modal modal-open">
          <div className="modal-box max-w-3xl">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-bold text-lg">New skill</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setCreating(false)}>
                ✕
              </button>
            </div>

            <label className="label">
              <span className="label-text font-medium">Name</span>
              <span className="label-text-alt text-base-content/60">
                lowercase, two or more words
              </span>
            </label>
            <input
              type="text"
              className="input input-bordered w-full font-mono mb-4"
              placeholder="processing-refunds"
              value={newName}
              onInput={(e) => setNewName((e.target as HTMLInputElement).value)}
            />

            <label className="label">
              <span className="label-text font-medium">Triggers when</span>
              <span
                className={`label-text-alt ${
                  newDescription.length > SKILL_DESCRIPTION_MAX_LENGTH
                    ? 'text-error'
                    : 'text-base-content/60'
                }`}
              >
                {newDescription.length}/{SKILL_DESCRIPTION_MAX_LENGTH}
              </span>
            </label>
            <textarea
              className="textarea textarea-bordered w-full mb-4"
              rows={2}
              placeholder="Use when a customer requests a refund, partial refund, or chargeback reversal"
              value={newDescription}
              onInput={(e) => setNewDescription((e.target as HTMLTextAreaElement).value)}
            />

            <label className="label">
              <span className="label-text font-medium">Instructions</span>
              <span className="label-text-alt text-base-content/60">
                markdown — loaded only when the skill matches
              </span>
            </label>
            <textarea
              className="textarea textarea-bordered w-full font-mono text-sm"
              rows={14}
              placeholder={'# Refunds\n\n1. Look up the order\n2. CC billing@\n'}
              value={newBody}
              onInput={(e) => setNewBody((e.target as HTMLTextAreaElement).value)}
            />

            <div className="modal-action">
              <button className="btn btn-ghost" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button
                className={`btn btn-primary ${savingNew ? 'loading' : ''}`}
                disabled={savingNew}
                onClick={handleCreate}
              >
                Create
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setCreating(false)}></div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Delete skill?</h3>
            <p className="py-4">
              <span className="font-mono">{deleteTarget.name}</span> will no longer be offered to
              your agents. Previous versions of its instructions are retained.
            </p>
            <div className="modal-action">
              <button className="btn btn-ghost" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button
                className={`btn btn-error ${deleting ? 'loading' : ''}`}
                disabled={deleting}
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setDeleteTarget(null)}></div>
        </div>
      )}
    </MainLayout>
  );
}
